// src/components/DesignPicker.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import "./DesignPicker.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

/* Optional outside/Escape close (parent can disable by passing closeOnOutsideOrEsc={false}) */
function useOutsideClose(ref, onClose) {
  useEffect(() => {
    if (!onClose) return;
    function onDown(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    }
    function onEsc(e) {
      if (e.key === "Escape") onClose?.();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [onClose, ref]);
}

const DesignPicker = forwardRef(function DesignPicker(
  { hideButton = false, closeOnOutsideOrEsc = false },
  ref
) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [active, setActive] = useState(-1); // keyboard focus index

  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useOutsideClose(wrapRef, closeOnOutsideOrEsc ? () => setOpen(false) : null);

  useImperativeHandle(
    ref,
    () => ({
      open: () => setOpen(true),
      close: () => setOpen(false),
      toggle: () => setOpen((v) => !v),
      isOpen: () => open,
    }),
    [open]
  );

  // Fetch once on first open
  useEffect(() => {
    if (!open || items.length) return;
    (async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(`${API_BASE_URL}/api/designs`);
        const ct = res.headers.get("content-type") || "";
        if (!ct.includes("application/json")) {
          const _text = await res.text();
          throw new Error("Unexpected response from /api/designs (not JSON).");
        }
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to fetch designs");

        const list = Array.isArray(data.items) ? data.items : [];
        setItems(list);
      } catch (e) {
        setError(e.message || "Failed to load");
      } finally {
        setLoading(false);
        // Focus search when panel opens
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    })();
  }, [open, items.length]);

  // Filter by query
  const filtered = useMemo(() => {
    const s = (q || "").trim().toLowerCase();
    if (!s) return items;
    return items.filter((d) => (d.name || "").toLowerCase().includes(s));
  }, [q, items]);

  // Keep active index valid
  useEffect(() => {
    if (filtered.length === 0) setActive(-1);
    else if (active >= filtered.length) setActive(filtered.length - 1);
  }, [filtered, active]);

  function pick(design) {
    window.dispatchEvent(new CustomEvent("design-picked", { detail: design }));
    setOpen(false);
  }

  // Keyboard navigation inside panel
  function onKeyDown(e) {
    if (!open) return;
    if (["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) e.preventDefault();

    if (e.key === "ArrowDown") {
      setActive((i) => {
        const ni = Math.min((i < 0 ? -1 : i) + 1, filtered.length - 1);
        scrollIntoView(ni);
        return ni;
      });
    } else if (e.key === "ArrowUp") {
      setActive((i) => {
        const ni = Math.max((i < 0 ? filtered.length : i) - 1, 0);
        scrollIntoView(ni);
        return ni;
      });
    } else if (e.key === "Enter" && active >= 0 && filtered[active]) {
      pick(filtered[active]);
    }
  }

  function scrollIntoView(idx) {
    const ul = listRef.current;
    if (!ul) return;
    const li = ul.querySelectorAll("li")[idx];
    if (li) li.scrollIntoView({ block: "nearest" });
  }

  return (
    <div ref={wrapRef} className="dp-wrap" onKeyDown={onKeyDown}>
      {!hideButton && (
        <button
          className="dp-trigger"
          title="Designs"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          {/* small folder glyph */}
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path
              d="M3 6h6l2 2h10v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z"
              fill="currentColor"
            />
          </svg>
        </button>
      )}

      {open && (
        <div className="dp-panel dp-animate" role="dialog" aria-label="Design picker">
          <div className="dp-search-row">
            <input
              ref={inputRef}
              className="dp-search"
              placeholder="Search designs..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          {loading && <div className="dp-status">Loading…</div>}

          {error && (
            <div className="dp-error" role="alert">
              {error}
              <div className="dp-error-sub">
                Tip: Set <code>REACT_APP_API_BASE_URL</code> and ensure
                <code> /api/designs </code> returns JSON like{" "}
                <code>{`{ items: [{name, dir, gds, cf}] }`}</code>.
              </div>
            </div>
          )}

          {!loading && !error && (
            <ul className="dp-list" ref={listRef} role="listbox">
              {filtered.length === 0 && (
                <li className="dp-empty">No matches</li>
              )}
              {filtered.map((d, i) => (
                // ...inside <ul className="dp-list"> map(...)
<li key={`${d.dir}-${d.kind}`}>
  <button
    className={`dp-item ${i === active ? "is-active" : ""}`}
    onClick={() => pick(d)}
    role="option"
    aria-selected={i === active}
    onMouseEnter={() => setActive(i)}
  >
    <div className="dp-item-name">
      <span className="dp-item-icon" aria-hidden="true">📁</span>
      {d.name}
      <span
        className={`dp-kind-badge ${d.kind === "drc" ? "is-drc" : "is-cf"}`}
        title={d.kind === "drc" ? "GDS + ERR pair" : "GDS + CF pair"}
        style={{
          marginLeft: 8,
          fontSize: 11,
          padding: "2px 6px",
          borderRadius: 8,
          background: d.kind === "drc" ? "rgba(0,150,255,.15)" : "rgba(16,185,129,.15)"
        }}
      >
        {d.kind === "drc" ? "🧪 DRC" : "🗨️ CF"}
      </span>
    </div>
    <div className="dp-item-sub">
      {d.gds} · {d.kind === "drc" ? d.err : d.cf}
    </div>
  </button>
</li>

              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
});

export default DesignPicker;
